import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  LinearProgress,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  People as PeopleIcon,
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Help as HelpIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, updateDoc, doc, serverTimestamp, query, where } from 'firebase/firestore';
import { db } from '../../firebaseconf';
import { useAuth } from '../../context/AuthContext';

export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [actionOpen, setActionOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [institutionHealth, setInstitutionHealth] = useState({});
  const [teacherAnalytics, setTeacherAnalytics] = useState([]);
  const [actionData, setActionData] = useState({
    action: 'suspend',
    reason: '',
    duration_days: 30
  });

  // Redirect if not super admin
  useEffect(() => {
    if (currentUser && currentUser.role !== 'super_admin') {
      navigate('/');
    }
  }, [currentUser, navigate]);

  // Fetch institutions with health data
  useEffect(() => {
    const fetchInstitutions = async () => {
      setLoading(true);
      try {
        const instCollection = collection(db, 'institutions');
        const snapshot = await getDocs(instCollection);
        const insts = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setInstitutions(insts);
        
        // Calculate health for each institution
        await calculateInstitutionHealth(insts);
      } catch (err) {
        console.error('Error fetching institutions:', err);
        setError('Failed to load institutions');
      } finally {
        setLoading(false);
      }
    };

    if (currentUser?.role === 'super_admin') {
      fetchInstitutions();
    }
  }, [currentUser]);

  // Calculate health metrics for each institution
  const calculateInstitutionHealth = async (insts) => {
    const healthData = {};
    
    for (const inst of insts) {
      try {
        // Get teacher count
        const teachersRef = collection(db, 'institutions', inst.id, 'teachers');
        const teachersSnapshot = await getDocs(teachersRef);
        const teacherCount = teachersSnapshot.docs.length;

        // Get student count
        const studentsRef = collection(db, 'institutions', inst.id, 'students');
        const studentsSnapshot = await getDocs(studentsRef);
        const studentCount = studentsSnapshot.docs.length;

        // Get assignments count
        const assignmentsRef = collection(db, 'institutions', inst.id, 'assignments');
        const assignmentsSnapshot = await getDocs(assignmentsRef);
        const assignmentCount = assignmentsSnapshot.docs.length;

        // Get attendance records count
        const attendanceRef = collection(db, 'institutions', inst.id, 'attendance');
        const attendanceSnapshot = await getDocs(attendanceRef);
        const attendanceCount = attendanceSnapshot.docs.length;

        // Calculate health score (0-100)
        let healthScore = 50; // Base score
        if (teacherCount > 0) healthScore += 15; // Has teachers
        if (studentCount > 0) healthScore += 15; // Has students
        if (assignmentCount > 0) healthScore += 10; // Has assignments
        if (attendanceCount > 0) healthScore += 10; // Has activity

        healthData[inst.id] = {
          teachers: teacherCount,
          students: studentCount,
          assignments: assignmentCount,
          attendance: attendanceCount,
          healthScore: Math.min(healthScore, 100),
          status: healthScore >= 70 ? 'healthy' : healthScore >= 40 ? 'warning' : 'critical'
        };
      } catch (err) {
        console.error(`Error calculating health for ${inst.id}:`, err);
        healthData[inst.id] = {
          teachers: 0,
          students: 0,
          assignments: 0,
          attendance: 0,
          healthScore: 0,
          status: 'unknown'
        };
      }
    }
    
    setInstitutionHealth(healthData);
  };

  // Fetch teacher analytics for selected institution
  const fetchTeacherAnalytics = async (instId) => {
    try {
      const teachersRef = collection(db, 'institutions', instId, 'teachers');
      const teachersSnapshot = await getDocs(teachersRef);
      
      const teacherData = [];
      for (const teacherDoc of teachersSnapshot.docs) {
        const teacher = teacherDoc.data();
        
        // Get students for this teacher
        const studentsRef = collection(db, 'institutions', instId, 'students');
        const studentsQuery = query(studentsRef, where('teacher_id', '==', teacherDoc.id));
        const studentsSnapshot = await getDocs(studentsQuery);
        
        // Get assignments for this teacher
        const assignmentsRef = collection(db, 'institutions', instId, 'assignments');
        const assignmentsQuery = query(assignmentsRef, where('teacher_id', '==', teacherDoc.id));
        const assignmentsSnapshot = await getDocs(assignmentsQuery);
        
        teacherData.push({
          id: teacherDoc.id,
          name: teacher.name || 'N/A',
          email: teacher.email || 'N/A',
          students: studentsSnapshot.docs.length,
          assignments: assignmentsSnapshot.docs.length,
          status: teacher.status || 'active'
        });
      }
      
      setTeacherAnalytics(teacherData);
    } catch (err) {
      console.error('Error fetching teacher analytics:', err);
      setError('Failed to load teacher analytics');
    }
  };

  const getHealthIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircleIcon sx={{ color: '#4CAF50' }} />;
      case 'warning':
        return <WarningIcon sx={{ color: '#FF9800' }} />;
      case 'critical':
        return <ErrorIcon sx={{ color: '#F44336' }} />;
      default:
        return <HelpIcon sx={{ color: '#999' }} />;
    }
  };

  const handleViewDetails = (institution) => {
    setSelectedInstitution(institution);
    setTeacherAnalytics([]); // Reset teacher analytics
    setDetailsOpen(true);
    // Load teacher analytics when opening details
    fetchTeacherAnalytics(institution.id);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedInstitution(null);
  };

  const handleActionOpen = (institution) => {
    setSelectedInstitution(institution);
    setActionOpen(true);
  };

  const handleActionClose = () => {
    setActionOpen(false);
    setActionData({ action: 'suspend', reason: '', duration_days: 30 });
  };

  const handleAction = async () => {
    try {
      if (!selectedInstitution?.id) return;

      const instRef = doc(db, 'institutions', selectedInstitution.id);
      const updates = {
        updatedAt: serverTimestamp()
      };

      if (actionData.action === 'suspend') {
        updates.status = 'suspended';
        updates.suspend_reason = actionData.reason;
        updates.suspend_until = new Date(Date.now() + actionData.duration_days * 24 * 60 * 60 * 1000).toISOString();
      } else if (actionData.action === 'activate') {
        updates.status = 'active';
        updates.suspend_reason = null;
        updates.suspend_until = null;
      }

      await updateDoc(instRef, updates);

      // Update local state
      setInstitutions(prev =>
        prev.map(inst =>
          inst.id === selectedInstitution.id
            ? { ...inst, ...updates }
            : inst
        )
      );

      setError('');
      handleActionClose();
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to perform action');
    }
  };

  const stats = {
    total_institutions: institutions.length,
    active_institutions: institutions.filter(i => i.status === 'active').length,
    total_users: institutions.reduce((sum, i) => sum + (i.current_users || 0), 0),
    total_storage: institutions.reduce((sum, i) => sum + (i.current_storage_gb || 0), 0).toFixed(2)
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          🚀 Super Admin Platform Dashboard
        </Typography>
        <Typography variant="body2" sx={{ color: '#666' }}>
          Monitor platform health, institutions, teachers, and system analytics
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Tabs for different views */}
      <Paper sx={{ mb: 4 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: '1px solid #e0e0e0' }}
        >
          <Tab label="📊 Overview" />
          <Tab label="🏢 Institutions" />
          <Tab label="📈 Institution Health" />
          <Tab label="👨‍🏫 Teacher Analytics" />
          <Tab label="❓ Help Center" />
        </Tabs>
      </Paper>

      {/* TAB 1: OVERVIEW */}
      {tabValue === 0 && (
        <>
          {/* Stats Grid */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        Total Institutions
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1 }}>
                        {stats.total_institutions}
                      </Typography>
                    </Box>
                    <BusinessIcon sx={{ fontSize: 40, color: '#2196F3', opacity: 0.5 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        Active Institutions
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1 }}>
                        {stats.active_institutions}
                      </Typography>
                    </Box>
                    <TrendingUpIcon sx={{ fontSize: 40, color: '#4CAF50', opacity: 0.5 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        Total Users
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1 }}>
                        {stats.total_users}
                      </Typography>
                    </Box>
                    <PeopleIcon sx={{ fontSize: 40, color: '#FF9800', opacity: 0.5 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        Total Storage
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1 }}>
                        {stats.total_storage} GB
                      </Typography>
                    </Box>
                    <SettingsIcon sx={{ fontSize: 40, color: '#9C27B0', opacity: 0.5 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Quick Stats */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                    📊 System Status
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <CheckCircleIcon sx={{ color: '#4CAF50' }} />
                    <Typography variant="body2">Platform Status: <strong>Operational</strong></Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <CheckCircleIcon sx={{ color: '#4CAF50' }} />
                    <Typography variant="body2">Database: <strong>Connected</strong></Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CheckCircleIcon sx={{ color: '#4CAF50' }} />
                    <Typography variant="body2">API: <strong>Operational</strong></Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                    ⚠️ Alerts & Warnings
                  </Typography>
                  {Object.entries(institutionHealth).filter(([_, health]) => health.status !== 'healthy').length > 0 ? (
                    <>
                      <Typography variant="body2" sx={{ color: '#F44336', mb: 1 }}>
                        🔴 {Object.entries(institutionHealth).filter(([_, health]) => health.status === 'critical').length} Critical Issues
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#FF9800' }}>
                        🟡 {Object.entries(institutionHealth).filter(([_, health]) => health.status === 'warning').length} Warnings
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" sx={{ color: '#4CAF50' }}>
                      ✅ All systems healthy
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {/* TAB 2: INSTITUTIONS */}
      {tabValue === 1 && (
        <Paper sx={{ mb: 4 }}>
          <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              All Institutions
            </Typography>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>Institution Name</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell align="center"><strong>Status</strong></TableCell>
                  <TableCell align="center"><strong>Users</strong></TableCell>
                  <TableCell align="center"><strong>Plan</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {institutions.length > 0 ? (
                  institutions.map((institution) => (
                    <TableRow key={institution.id} sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {institution.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#999' }}>
                          {institution.city}, {institution.state}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={institution.type?.toUpperCase()}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={institution.status || 'active'}
                          size="small"
                          color={institution.status === 'suspended' ? 'error' : 'success'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {institution.current_users || 0}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {institution.plan?.toUpperCase() || 'BASIC'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Button
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleViewDetails(institution)}
                          >
                            View
                          </Button>
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => handleActionOpen(institution)}
                            color={institution.status === 'suspended' ? 'success' : 'warning'}
                          >
                            {institution.status === 'suspended' ? 'Activate' : 'Suspend'}
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" sx={{ color: '#999' }}>
                        No institutions found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* TAB 3: INSTITUTION HEALTH */}
      {tabValue === 2 && (
        <Grid container spacing={3}>
          {institutions.map((inst) => {
            const health = institutionHealth[inst.id] || {};
            return (
              <Grid item xs={12} md={6} key={inst.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {inst.name}
                      </Typography>
                      {getHealthIcon(health.status)}
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Health Score</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {health.healthScore || 0}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={health.healthScore || 0}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            backgroundColor: health.status === 'healthy' ? '#4CAF50' : health.status === 'warning' ? '#FF9800' : '#F44336'
                          }
                        }}
                      />
                    </Box>

                    <Typography variant="body2" sx={{ color: '#666', mb: 2, fontWeight: 'bold' }}>
                      Metrics:
                    </Typography>

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#999' }}>Teachers</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {health.teachers || 0}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#999' }}>Students</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {health.students || 0}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#999' }}>Assignments</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {health.assignments || 0}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#999' }}>Activity Records</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {health.attendance || 0}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ mt: 2, p: 1, bgcolor: health.status === 'healthy' ? '#e8f5e9' : health.status === 'warning' ? '#fff3e0' : '#ffebee', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        Status: <span style={{ color: health.status === 'healthy' ? '#4CAF50' : health.status === 'warning' ? '#FF9800' : '#F44336' }}>
                          {health.status?.toUpperCase()}
                        </span>
                      </Typography>
                      {health.status === 'warning' && (
                        <Typography variant="caption" sx={{ color: '#FF9800' }}>
                          ⚠️ Institution needs attention
                        </Typography>
                      )}
                      {health.status === 'critical' && (
                        <Typography variant="caption" sx={{ color: '#F44336' }}>
                          🔴 Critical: Please review immediately
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* TAB 4: TEACHER ANALYTICS */}
      {tabValue === 3 && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
              Select an institution to view teacher analytics
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              {institutions.map((inst) => (
                <Grid item xs={12} sm={6} md={4} key={inst.id}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      setSelectedInstitution(inst);
                      fetchTeacherAnalytics(inst.id);
                    }}
                    sx={{
                      p: 2,
                      textAlign: 'left',
                      backgroundColor: selectedInstitution?.id === inst.id ? '#e3f2fd' : 'transparent'
                    }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {inst.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#999' }}>
                        {inst.city}, {inst.state}
                      </Typography>
                    </Box>
                  </Button>
                </Grid>
              ))}
            </Grid>

            {selectedInstitution && teacherAnalytics.length > 0 && (
              <Paper>
                <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Teachers in {selectedInstitution.name}
                  </Typography>
                </Box>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell><strong>Teacher Name</strong></TableCell>
                        <TableCell><strong>Email</strong></TableCell>
                        <TableCell align="center"><strong>Students</strong></TableCell>
                        <TableCell align="center"><strong>Assignments</strong></TableCell>
                        <TableCell align="center"><strong>Status</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {teacherAnalytics.map((teacher) => (
                        <TableRow key={teacher.id}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {teacher.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{teacher.email}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={teacher.students} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={teacher.assignments} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={teacher.status}
                              size="small"
                              color={teacher.status === 'active' ? 'success' : 'error'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {selectedInstitution && teacherAnalytics.length === 0 && (
              <Alert severity="info">
                No teachers found for this institution yet
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* TAB 5: HELP CENTER */}
      {tabValue === 4 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  ❓ Help Center & Support
                </Typography>

                <List>
                  <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', pb: 2, borderBottom: '1px solid #e0e0e0' }}>
                    <ListItemText
                      primary="📊 How to Monitor Institution Health"
                      secondary="Go to 'Institution Health' tab to see real-time health scores, metrics, and alerts for all institutions. Health scores are calculated based on teacher count, student count, assignments, and activity records."
                      primaryTypographyProps={{ sx: { fontWeight: 'bold', mb: 1 } }}
                    />
                  </ListItem>

                  <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', pb: 2, borderBottom: '1px solid #e0e0e0' }}>
                    <ListItemText
                      primary="👨‍🏫 How to View Teacher Analytics"
                      secondary="Select an institution from the Teacher Analytics tab to see all teachers, their student counts, number of assignments created, and their current status. Use this to identify underperforming or overloaded teachers."
                      primaryTypographyProps={{ sx: { fontWeight: 'bold', mb: 1 } }}
                    />
                  </ListItem>

                  <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', pb: 2, borderBottom: '1px solid #e0e0e0' }}>
                    <ListItemText
                      primary="🚫 How to Suspend an Institution"
                      secondary="Go to the Institutions tab, click Edit button, select 'Suspend', provide a reason and duration. The institution will be locked until you activate it again."
                      primaryTypographyProps={{ sx: { fontWeight: 'bold', mb: 1 } }}
                    />
                  </ListItem>

                  <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', pb: 2, borderBottom: '1px solid #e0e0e0' }}>
                    <ListItemText
                      primary="⚠️ Understanding Health Status"
                      secondary="🟢 Healthy (70-100%): All systems functioning well. 🟡 Warning (40-69%): Some metrics need attention. 🔴 Critical (<40%): Immediate action required."
                      primaryTypographyProps={{ sx: { fontWeight: 'bold', mb: 1 } }}
                    />
                  </ListItem>

                  <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                    <ListItemText
                      primary="📞 Contact Support"
                      secondary="For technical issues, email: support@platform.com | For sales: sales@platform.com"
                      primaryTypographyProps={{ sx: { fontWeight: 'bold', mb: 1 } }}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  📋 Platform Features
                </Typography>
                <List dense>
                  <ListItem>✅ Real-time institution monitoring</ListItem>
                  <ListItem>✅ Teacher performance tracking</ListItem>
                  <ListItem>✅ Student analytics</ListItem>
                  <ListItem>✅ Assignment & attendance tracking</ListItem>
                  <ListItem>✅ Multi-institution management</ListItem>
                  <ListItem>✅ Suspension & activation controls</ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  🔧 System Requirements
                </Typography>
                <List dense>
                  <ListItem>📱 Modern web browser (Chrome, Firefox, Safari)</ListItem>
                  <ListItem>🔗 Stable internet connection</ListItem>
                  <ListItem>🔐 Super Admin authentication</ListItem>
                  <ListItem>💾 Firestore database access</ListItem>
                  <ListItem>⚡ Firebase configuration loaded</ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Institution Details Dialog */}
      <Dialog open={detailsOpen} onClose={handleCloseDetails} maxWidth="md" fullWidth>
        <DialogTitle>Institution Details & Teacher Analytics</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedInstitution && (
            <Grid container spacing={3}>
              {/* Institution Info */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Institution Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#999' }}>Name</Typography>
                      <Typography variant="body1">{selectedInstitution.name}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#999' }}>Type</Typography>
                      <Typography variant="body1">{selectedInstitution.type}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#999' }}>Status</Typography>
                      <Typography variant="body1">{selectedInstitution.status}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#999' }}>Email</Typography>
                      <Typography variant="body1">{selectedInstitution.email}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#999' }}>Address</Typography>
                      <Typography variant="body1">
                        {selectedInstitution.address}, {selectedInstitution.city}, {selectedInstitution.state}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#999' }}>Users</Typography>
                      <Typography variant="body1">{selectedInstitution.current_users || 0}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#999' }}>Plan</Typography>
                      <Typography variant="body1">{selectedInstitution.plan || 'basic'}</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>

              {/* Health Status */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
                  📊 Health Status
                </Typography>
                {institutionHealth[selectedInstitution.id] && (
                  <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Health Score</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {institutionHealth[selectedInstitution.id].healthScore}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={institutionHealth[selectedInstitution.id].healthScore}
                      sx={{ mb: 2 }}
                    />
                    <Grid container spacing={1}>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" sx={{ color: '#999' }}>Teachers</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {institutionHealth[selectedInstitution.id].teachers}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" sx={{ color: '#999' }}>Students</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {institutionHealth[selectedInstitution.id].students}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" sx={{ color: '#999' }}>Assignments</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {institutionHealth[selectedInstitution.id].assignments}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" sx={{ color: '#999' }}>Activity</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {institutionHealth[selectedInstitution.id].attendance}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Grid>

              {/* Teachers Analytics */}
              {teacherAnalytics.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
                    👨‍🏫 Teachers in This Institution
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell><strong>Name</strong></TableCell>
                          <TableCell><strong>Students</strong></TableCell>
                          <TableCell><strong>Assignments</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {teacherAnalytics.map((teacher) => (
                          <TableRow key={teacher.id}>
                            <TableCell>{teacher.name}</TableCell>
                            <TableCell>{teacher.students}</TableCell>
                            <TableCell>{teacher.assignments}</TableCell>
                            <TableCell>
                              <Chip
                                label={teacher.status}
                                size="small"
                                color={teacher.status === 'active' ? 'success' : 'error'}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Action Dialog */}
      <Dialog open={actionOpen} onClose={handleActionClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionData.action === 'suspend' ? 'Suspend Institution' : 'Activate Institution'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            {selectedInstitution?.status !== 'suspended' && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Reason"
                    value={actionData.reason}
                    onChange={(e) => setActionData(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder="Reason for suspension"
                    multiline
                    rows={3}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="text"
                    label="Suspension Duration (Days)"
                    value={actionData.duration_days}
                    onChange={(e) => setActionData(prev => ({ ...prev, duration_days: parseInt(e.target.value) }))}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleActionClose}>Cancel</Button>
          <Button onClick={handleAction} variant="contained">
            {actionData.action === 'suspend' ? 'Suspend' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
